// The operations console (plan §7): shell, site switcher, routing and keyboard shortcuts.
import { ViewingBanner } from "../support/ViewAccess.tsx";
import { SupportPage } from "../support/HelpDesk.tsx";
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Permission } from "@vavr/core";
import { ThemeToggle, AppShell, Button, EmptyState, Icon, Menu, ToastProvider, Wordmark, initials, type IconName, type NavItem } from "@vavr/ui";
import { api, match, navigate, setEnabledModules, slot, usePath } from "../shared/index.ts";
import { ApiCache, CacheContext, ConsoleContext, makeCan, store, useConsole, useGet, type ConsoleCtx, type Me } from "./data.tsx";
import { LoadError, Loading, ROLE_LABEL } from "./common.tsx";
import { Board } from "./Board.tsx";
import { ChildrenList, ChildDetail } from "./Children.tsx";
import { Adults } from "./Adults.tsx";
import { Import } from "./Import.tsx";
import { Rooms } from "./Rooms.tsx";
import { Staff } from "./Staff.tsx";
import { Devices } from "./Devices.tsx";
import { EventsList, EventDetail } from "./Events.tsx";
import { Requests } from "./Requests.tsx";
import { Reports } from "./Reports.tsx";
import { Settings } from "./Settings.tsx";
import { Integrations } from "./Integrations.tsx";
import { Audit } from "./Audit.tsx";
import { ConsoleLive } from "./live.tsx";
import { AccountMenu, NotificationBell, SearchButton } from "./chrome.tsx";
import { GlobalSearch } from "./Search.tsx";
import { Profile } from "./Profile.tsx";

interface Section { id: string; label: string; icon: IconName; href: string; key: string; allowed: (can: (p: Permission) => boolean) => boolean; orgKind?: "school" | "operator"; group: string }

/** Sidebar headings, in order; a module's section goes under Operations unless it says otherwise. */
const GROUPS = ["Daily", "People", "Operations", "Transport", "Administration"];

export const SECTIONS: Section[] = [
  { id: "board", label: "Board", icon: "home", href: "/console", key: "b", allowed: (c) => c("roster.read"), group: "Daily" },
  { id: "children", label: "Children", icon: "child", href: "/console/children", key: "c", allowed: (c) => c("roster.read"), group: "People" },
  { id: "adults", label: "Adults", icon: "user", href: "/console/adults", key: "a", allowed: (c) => c("roster.write"), group: "People" },
  { id: "import", label: "Import", icon: "upload", href: "/console/import", key: "i", allowed: (c) => c("roster.write"), group: "People" },
  { id: "rooms", label: "Rooms", icon: "list", href: "/console/rooms", key: "o", allowed: (c) => c("roster.read"), group: "People" },
  { id: "staff", label: "Staff", icon: "shield", href: "/console/staff", key: "s", allowed: (c) => c("staff.manage"), group: "People" },
  { id: "devices", label: "Devices", icon: "qr", href: "/console/devices", key: "d", allowed: (c) => c("device.enrol"), group: "Operations" },
  { id: "events", label: "Events", icon: "clock", href: "/console/events", key: "e", allowed: (c) => c("events.read_all") || c("events.read_class"), group: "Daily" },
  { id: "requests", label: "Requests", icon: "bell", href: "/console/requests", key: "q", allowed: (c) => c("roster.read"), group: "Daily" },
  { id: "reports", label: "Reports", icon: "signature", href: "/console/reports", key: "r", allowed: (c) => c("reports.read") && c("events.read_all"), group: "Administration" },
  { id: "integrations", label: "Integrations", icon: "link", href: "/console/integrations", key: "k", allowed: (c) => c("integrations.manage"), group: "Administration" },
  { id: "settings", label: "Settings", icon: "settings", href: "/console/settings", key: "t", allowed: () => true, group: "Administration" },
  { id: "audit", label: "Audit", icon: "lock", href: "/console/audit", key: "u", allowed: (c) => c("audit.read"), group: "Administration" },
];

/** Sections about children: not shown at a bus company's depot. */
const SCHOOL_ONLY = new Set(["board", "children", "adults", "import", "rooms", "events", "requests", "reports"]);

/** Core sections plus the ones modules add through the "console.nav" slot (before Settings). */
export function sections(): Section[] {
  // Two modules may register the same page (Safety reports): it shows once.
  const mods: Section[] = slot("console.nav").filter((m, i, all) => all.findIndex((x) => x.id === m.id) === i).map((m) => ({
    id: m.id, label: m.label, icon: m.icon, href: `/console/${m.path}`, key: m.key ?? "", allowed: (c) => !m.permission || c(m.permission), orgKind: m.orgKind, group: m.group ?? "Operations",
  }));
  const at = SECTIONS.findIndex((x) => x.id === "settings");
  const all = [...SECTIONS.slice(0, at), ...mods, ...SECTIONS.slice(at)];
  const rank = (g: string) => (GROUPS.indexOf(g) + GROUPS.length + 1) % (GROUPS.length + 1);
  return all.map((s, i) => [s, i] as const).sort(([a, i], [b, j]) => rank(a.group) - rank(b.group) || i - j).map(([s]) => s);
}

function route(path: string): { id: string; title: string; render: () => ReactNode } {
  let m: Record<string, string> | null;
  if ((m = match("/console/children/:id", path))) { const id = m.id!; return { id: "children", title: "Child", render: () => <ChildDetail id={id} /> }; }
  if ((m = match("/console/events/:id", path))) { const id = m.id!; return { id: "events", title: "Event", render: () => <EventDetail id={id} /> }; }
  const table: Record<string, [string, string, () => ReactNode]> = {
    "/console": ["board", "Board", () => <Board />],
    "/console/children": ["children", "Children", () => <ChildrenList />],
    "/console/adults": ["adults", "Adults", () => <Adults />],
    "/console/import": ["import", "Import a roster", () => <Import />],
    "/console/rooms": ["rooms", "Rooms", () => <Rooms />],
    "/console/staff": ["staff", "Staff", () => <Staff />],
    "/console/devices": ["devices", "Devices", () => <Devices />],
    "/console/events": ["events", "Events", () => <EventsList />],
    "/console/requests": ["requests", "Requests", () => <Requests />],
    "/console/reports": ["reports", "Reports", () => <Reports />],
    "/console/integrations": ["integrations", "Integrations", () => <Integrations />],
    "/console/settings": ["settings", "Settings", () => <Settings />],
    "/console/audit": ["audit", "Audit log", () => <Audit />],
    "/console/profile": ["profile", "Profile", () => <Profile />],
    "/console/support": ["support", "Help and support", () => <SupportPage />],
  };
  for (const m of slot("console.nav")) table[`/console/${m.path}`] = [m.id, m.title, m.render];
  const hit = table[path.replace(/\/$/, "") || "/console"];
  return hit ? { id: hit[0], title: hit[1], render: hit[2] } : { id: "", title: "Not found", render: () => <EmptyState title="There is no page here" description="Check the address, or choose a section on the left." /> };
}

/** Mounted for every /console path. `path` and `cache` exist for tests. */
export function ConsoleApp({ path, cache }: { path?: string; cache?: ApiCache }) {
  const [c] = useState(() => cache ?? new ApiCache());
  return (
    <CacheContext.Provider value={c}>
      <ToastProvider>
        <Shell forcedPath={path} />
      </ToastProvider>
    </CacheContext.Provider>
  );
}

function Shell({ forcedPath }: { forcedPath?: string }) {
  const livePath = usePath();
  const path = forcedPath ?? livePath;
  const me = useGet<Me>("/api/v1/me", 60_000);
  const [chosen, setChosen] = useState<string | null>(() => store.get("pmu.console.site"));

  const ctx = useMemo<ConsoleCtx | null>(() => {
    if (!me.data?.sites.length) return null;
    const site = me.data.sites.find((s) => s.id === chosen) ?? me.data.sites[0]!;
    return {
      me: me.data, site, base: `/api/v1/sites/${site.id}`, can: makeCan(me.data, site),
      setSite: (id) => { store.set("pmu.console.site", id); setChosen(id); navigate("/console"); },
    };
  }, [me.data, chosen]);

  // Only the modules this organisation has (older servers send no list: everything shows).
  if (ctx) setEnabledModules((id) => ctx.me.modules?.[ctx.site.org_id]?.includes(id) ?? true);
  const r = route(path);
  const all = sections();
  // A bus company's depot (transport slice 5) has staff, phones and routes, never children.
  const depot = ctx?.site.org_kind === "operator";
  // A school's settings and a bus company's stay on their own side (owner, 2026-10-01).
  const shows = (s: Section) => !!ctx && s.allowed(ctx.can) && !(depot && SCHOOL_ONLY.has(s.id)) && (!s.orgKind || s.orgKind === (depot ? "operator" : "school"));
  const visible = all.filter(shows);
  const nav: NavItem[] = visible.map((s) => ({ id: s.id, label: s.label, icon: s.icon, href: s.href, group: s.group }));
  const allowedKeys = visible.map((s) => s.id).join();
  const [searching, setSearching] = useState(false);
  const openSearch = useCallback(() => setSearching(true), []);
  useEffect(() => { if (depot && (path === "/console" || path === "/console/")) navigate("/console/transport", true); }, [depot, path]);
  useShortcuts(allowedKeys, openSearch);
  useEffect(() => { if (typeof document !== "undefined") document.title = `${r.title} · ${ctx?.site.name ?? "Console"}`; }, [r.title, ctx?.site.name]);

  if (me.loading) return <Loading label="Loading the console" />;
  if (me.error) return <div className="pmu-page"><LoadError error={me.error} retry={me.reload} /></div>;
  if (!ctx) return <div className="pmu-page"><EmptyState icon="home" title="No site on this account" description="Ask your organization administrator to invite you to a site, or start a new one." action={me.data?.platform ? <Button onClick={() => navigate("/platform")}>Open the platform console</Button> : <Button onClick={() => navigate("/signup")}>Start a site</Button>} /></div>;

  const section = all.find((s) => s.id === r.id);
  const allowed = section ? shows(section) : true;
  return (
    <ConsoleContext.Provider value={ctx}>
      <AppShell
        brand={<Wordmark withMark />}
        title={r.title}
        nav={nav}
        activeId={r.id}
        onNavigate={(id, e) => { const s = all.find((x) => x.id === id); if (s && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) { e.preventDefault(); navigate(s.href); } }}
        topBar={<TopBar onSearch={openSearch} />}
        sideHeader={<Workspace />}
        className={`pmu-console${slot("console.nav").some((m) => m.id === r.id && m.fullBleed) ? " pmu-console--bleed" : ""}`}
      >
        <ViewingBanner view={ctx.me.view} name={ctx.me.account.name} />
        {allowed ? <ConsoleLive base={ctx.base}><div key={ctx.site.id}>{r.render()}</div></ConsoleLive> : <EmptyState icon="lock" title="Not available to your role" description="Ask a site administrator if you need this." />}
      </AppShell>
      <GlobalSearch open={searching} onOpenChange={setSearching} pages={[...visible, { id: "profile", label: "Your profile", icon: "user" as IconName, href: "/console/profile" }]} />
      {slot("console.overlay").filter((o) => !o.permission || ctx.can(o.permission)).map((o) => <Fragment key={o.id}>{o.render({ siteId: ctx.site.id })}</Fragment>)}
    </ConsoleContext.Provider>
  );
}

/** The highest role this person holds here, for the account button. */
function roleHere(me: Me, site: { id: string; org_id: string }) {
  const order = ["platform_operator", "org_admin", "site_admin", "transport:dispatcher", "door_staff", "teacher", "transport:driver", "auditor"];
  const held = me.memberships.filter((m) => (m.org_id === site.org_id && (m.site_id == null || m.site_id === site.id)) || m.role === "platform_operator").map((m) => m.role as string);
  const top = order.find((r) => held.includes(r));
  return top ? ROLE_LABEL[top] ?? top : "";
}

function TopBar({ onSearch }: { onSearch: () => void }) {
  const { me, site, setSite } = useConsole();
  return (
    <div className="pmu-c-topbar">
      <SearchButton onClick={onSearch} />
      <NotificationBell open={(n) => {
        // A notification from another of this person's sites: switch to it first.
        if (n.site_id && n.site_id !== site.id && me.sites.some((x) => x.id === n.site_id)) setSite(n.site_id);
        if (n.href) navigate(n.href);
      }} />
      <ThemeToggle />
      <AccountMenu me={me} role={roleHere(me, site)} items={[{ id: "support", label: "Help and support", icon: "message", onSelect: () => navigate("/console/support") }, ...(me.platform ? [{ id: "platform", label: "Platform console", icon: "shield" as const, onSelect: () => navigate("/platform") }] : [])]} />
    </div>
  );
}

const SITE_KIND: Record<string, string> = { childcare: "Childcare center", school: "School", district: "District", camp: "Camp" };

/** The workspace (site) card at the top of the sidebar; a switcher when the account has several. */
function Workspace() {
  const { me, site, setSite } = useConsole();
  const kind = site.org_kind === "operator" ? "Bus company" : SITE_KIND[site.kind] ?? "School";
  const many = me.sites.length > 1;
  const body = (
    <>
      <span className="pmu-c-workspace__mark" aria-hidden="true">{initials(site.name)}</span>
      <span className="pmu-c-workspace__text"><span className="pmu-c-workspace__name">{site.name}</span><span className="pmu-c-workspace__kind">{kind}{site.state ? ` · ${site.state}` : ""}</span></span>
      {many && <Icon name="chevron-down" className="pmu-c-workspace__chevron" />}
    </>
  );
  if (!many) return <div className="pmu-c-workspace">{body}</div>;
  return (
    <Menu
      align="start" trigger={<button type="button" className="pmu-c-workspace pmu-c-workspace--button" aria-label={`Site: ${site.name}. Switch site`}>{body}</button>}
      header={<div className="pmu-c-workspace-head">Switch site</div>}
      groups={[me.sites.map((s) => ({ id: s.id, label: s.name, icon: (s.id === site.id ? "check" : undefined) as IconName | undefined, hint: s.org_kind === "operator" ? "Bus company" : undefined, onSelect: () => setSite(s.id) }))]}
    />
  );
}

/**
 * Ctrl/⌘ K opens global search from anywhere; "/" focuses the page's own search box, or opens
 * global search where there is none; "g" then a letter jumps to a section (docs/spec/console.md).
 */
function useShortcuts(allowedIds: string, openSearch: () => void) {
  useEffect(() => {
    const list = sections().filter((s) => allowedIds.split(",").includes(s.id));
    let pendingG = 0;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") { e.preventDefault(); openSearch(); return; }
      if (e.metaKey || e.ctrlKey || e.altKey || (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)))) return;
      if (e.key === "/") {
        const box = document.querySelector<HTMLInputElement>("[data-search]");
        e.preventDefault();
        if (box) box.focus(); else openSearch();
        return;
      }
      if (e.key === "g") { pendingG = Date.now(); return; }
      if (Date.now() - pendingG < 1500) {
        const s = list.find((x) => x.key === e.key);
        pendingG = 0;
        if (s) { e.preventDefault(); navigate(s.href); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowedIds, openSearch]);
}
